import React, { useState } from "react";
import { Alert, Button, Space, Spin, Modal, FloatButton } from "antd";
import { EditOutlined, SaveOutlined, CloseOutlined, ReloadOutlined } from "@ant-design/icons";
import DashboardGrid from "./components/DashboardGrid";
import "./index.css";
import { useDashboardLayout } from "./hooks/useDashboardLayout";
import { useAuth } from "../../hooks/useAuth";
import { ToolbarHelpButton } from "../../components/common/HelpButton";

const DashboardPage = () => {
    const { user } = useAuth();
    const { layout, loading, error, saveLayout, resetLayout } = useDashboardLayout();
    const [isEditing, setIsEditing] = useState(false);
    const [currentLayout, setCurrentLayout] = useState([]);

    // 当从 API 加载布局后，同步到本地状态
    React.useEffect(() => {
        if (layout) {
            setCurrentLayout(layout);
        }
    }, [layout]);

    const handleLayoutChange = (newLayout) => {
        setCurrentLayout(newLayout);
    };

    const handleRemoveItem = (itemId) => {
        const newLayout = currentLayout.filter((item) => item.i !== itemId);
        setCurrentLayout(newLayout);
    };

    const handleWidgetRequestResize = React.useCallback((widgetId, desiredH) => {
        setCurrentLayout((prev) => {
            const item = prev.find((it) => it.i === widgetId);
            if (!item || item.h === desiredH) return prev;
            return prev.map((it) => (it.i === widgetId ? { ...it, h: desiredH } : it));
        });
    }, []);

    const handleSave = () => {
        saveLayout(currentLayout);
        setIsEditing(false);
    };

    const handleReset = () => {
        Modal.confirm({
            title: "确认重置布局?",
            content: "这将删除您的自定义布局，恢复为系统默认或角色模版。此操作无法撤销。",
            okText: "确认重置",
            cancelText: "取消",
            onOk: async () => {
                await resetLayout();
                setIsEditing(false);
            },
        });
    };

    const handleCancel = () => {
        setCurrentLayout(layout); // 恢复到原始布局
        setIsEditing(false);
    };

    if (loading) {
        return (
            <div className="page-container" style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
                <Spin size="large" tip="加载工作台..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className="page-container">
                <Alert message="加载失败" description="无法加载工作台配置，请稍后重试。" type="error" showIcon />
            </div>
        );
    }

    return (
        <div className="page-container dashboard-page" style={{ position: "relative", minHeight: "100vh" }}>
            <div className="dashboard-content" style={{ overflowX: "hidden" }}>
                {isEditing && <Alert message="编辑模式" description="您可以拖拽卡片调整位置，或拖动右下角调整大小。完成后请点击右下角的保存。" type="info" showIcon style={{ margin: "16px 24px" }} />}

                <DashboardGrid layout={currentLayout} onLayoutChange={handleLayoutChange} isDraggable={isEditing} isResizable={isEditing} onRemoveItem={handleRemoveItem} onWidgetRequestResize={handleWidgetRequestResize} />
            </div>

            {/* Floating Action Button for Edit Mode */}
            <div
                style={{
                    position: "fixed",
                    bottom: "40px",
                    right: "40px",
                    zIndex: 1000,
                    display: "flex",
                    flexDirection: "column", // 垂直排列
                    alignItems: "center", // 居中对齐
                    gap: "12px",
                }}
            >
                {isEditing ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px", alignItems: "center" }}>
                        <ToolbarHelpButton
                            contentPath="help/dashboard-layout-edit.md"
                            title="布局编辑说明"
                            tooltip="查看布局编辑说明"
                            shape="circle"
                            style={{
                                width: "40px",
                                height: "40px",
                                minWidth: "40px",
                                borderRadius: "50%",
                                padding: 0,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                backgroundColor: "#fff",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                            }}
                        />
                        <FloatButton
                            icon={<ReloadOutlined style={{ color: "#ff4d4f" }} />}
                            onClick={handleReset}
                            tooltip="重置默认布局"
                            style={{
                                width: "40px",
                                height: "40px",
                                position: "static",
                            }}
                        />
                        <FloatButton
                            icon={<CloseOutlined />}
                            onClick={handleCancel}
                            tooltip="取消编辑"
                            style={{
                                width: "40px",
                                height: "40px",
                                position: "static",
                            }}
                        />
                        <FloatButton
                            type="primary"
                            icon={<SaveOutlined />}
                            onClick={handleSave}
                            tooltip="保存布局"
                            style={{
                                width: "56px",
                                height: "56px",
                                position: "static",
                            }}
                        />
                    </div>
                ) : (
                    <>
                        {/* 帮助按钮 - 作为次要操作悬浮在主按钮上方 */}
                        <ToolbarHelpButton
                            contentPath="help/dashboard-layout-edit.md"
                            title="布局编辑说明"
                            tooltip="查看布局编辑说明"
                            shape="circle" // 确保也是正圆
                            style={{
                                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                height: "40px",
                                width: "40px",
                                minWidth: "40px", // 防止被压缩
                                padding: 0,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                backgroundColor: "#fff",
                                marginBottom: "12px", // 显式间距
                                opacity: 0.8,
                                transition: "opacity 0.3s",
                                borderRadius: "50%",
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
                            onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.8)}
                            hideAfterViewed={true}
                        />

                        {/* 编辑按钮 -作为主要操作，使用FloatButton确保正圆 */}
                        <FloatButton
                            type="primary"
                            icon={<EditOutlined />}
                            onClick={() => setIsEditing(true)}
                            tooltip="编辑布局"
                            style={{
                                width: "56px",
                                height: "56px",
                                position: "static", //为了在flex容器中堆叠，取消默认的fixed定位
                                opacity: 0.8,
                                transition: "opacity 0.3s",
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
                            onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.8)}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

export default DashboardPage;
